import type {
  ClientOrganization,
  ClientOrganizationUpdateRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevineOrganizationApi } from '@/features/api/hooks/grapevine-organization/useGrapevineOrganizationApi';
import { useApiMutation } from '@/features/api/hooks/useApiMutation';

type UpdateOrganizationVariables = {
  organizationId: string;
  clientOrganizationUpdateRequest: ClientOrganizationUpdateRequest;
};

export const UPDATE_ORGANIZATION_MUTATION_KEY = ['updateOrganization'] as const;

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  const api = useGrapevineOrganizationApi();

  return useApiMutation<
    ClientOrganization,
    unknown,
    UpdateOrganizationVariables
  >({
    mutationKey: UPDATE_ORGANIZATION_MUTATION_KEY,
    mutationFn: ({ organizationId, clientOrganizationUpdateRequest }) =>
      api.updateOrganization({
        organizationId,
        clientOrganizationUpdateRequest
      }),
    successMessageKey: 'organization.updateSuccess',
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.OWN_ORGANIZATIONS]
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.POST_CREATION_OPTIONS]
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.ORGANIZATIONS]
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.ORGANIZATION, variables.organizationId]
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.ORGANIZATIONS_IN_GEO_AREA]
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.ORGANIZATIONS_IN_OWN_GEO_AREA_SELECTION]
      });
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.TERRITORIAL_AUTHORITY]
      });
    }
  });
};

export const useUpdateOrganizationPending = (): boolean => {
  const active = useIsMutating({
    mutationKey: [UPDATE_ORGANIZATION_MUTATION_KEY]
  });

  return active > 0;
};
